import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  useTheme,
  alpha,
  Stack,
  Avatar,
  Chip,
  Grid,
  LinearProgress,
  IconButton,
  Fade,
  Zoom,
  Dialog,
  DialogContent,
  Paper
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PlayArrow,
  CheckCircle,
  LocationOn,
  NotificationImportant,
  Analytics,
  Speed,
  AutoAwesome,
  ArrowForward,
  EmojiEvents,
  Close
} from '@mui/icons-material';
import { useTranslate } from '@refinedev/core';
import { VideoPlayer } from './VideoPlayer';

interface FeatureDemoWalaxyProps {
  onComplete: () => void;
  companyData: any;
}

interface Feature {
  id: string;
  title: string;
  description: string;
  emoji: string;
  icon: any;
  color: string;
  demo: {
    title: string;
    items: Array<{
      label: string;
      value: string | number;
      trend?: 'up' | 'down';
    }>;
  };
}

export const FeatureDemoWalaxy: React.FC<FeatureDemoWalaxyProps> = ({ onComplete, companyData }) => {
  const theme = useTheme();
  const translate = useTranslate();
  const [activeFeature, setActiveFeature] = useState<string | null>(null);
  const [completedFeatures, setCompletedFeatures] = useState<string[]>([]);
  const [autoPlayIndex, setAutoPlayIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(false);
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [currentVideo, setCurrentVideo] = useState<'company' | 'dashboard' | 'monitoring' | null>(null);
  const [showIntroVideo, setShowIntroVideo] = useState(true);

  const features: Feature[] = [
    {
      id: 'tracking',
      title: translate('onboarding.demo.features.company.title'),
      description: translate('onboarding.demo.features.company.description'),
      emoji: '🏢',
      icon: LocationOn,
      color: theme.palette.primary.main,
      demo: {
        title: translate('onboarding.demo.features.company.demoTitle'),
        items: [
          { label: translate('onboarding.demo.features.company.teams'), value: translate('onboarding.demo.features.company.teamsValue'), trend: 'up' },
          { label: translate('onboarding.demo.features.company.members'), value: translate('onboarding.demo.features.company.membersValue'), trend: 'up' },
          { label: translate('onboarding.demo.features.company.invitations'), value: translate('onboarding.demo.features.company.invitationsValue'), trend: 'down' }
        ]
      }
    },
    {
      id: 'alerts',
      title: translate('onboarding.demo.features.dashboard.title'),
      description: translate('onboarding.demo.features.dashboard.description'),
      emoji: '📊',
      icon: NotificationImportant,
      color: theme.palette.warning.main,
      demo: {
        title: translate('onboarding.demo.features.dashboard.demoTitle'),
        items: [
          { label: translate('onboarding.demo.features.dashboard.alertsToday'), value: translate('onboarding.demo.features.dashboard.alertsValue') },
          { label: translate('onboarding.demo.features.dashboard.responseTime'), value: translate('onboarding.demo.features.dashboard.responseValue') },
          { label: translate('onboarding.demo.features.dashboard.activityRate'), value: translate('onboarding.demo.features.dashboard.activityValue') }
        ]
      }
    },
    {
      id: 'analytics',
      title: translate('onboarding.demo.features.monitoring.title'),
      description: translate('onboarding.demo.features.monitoring.description'),
      emoji: '🛡️',
      icon: Analytics,
      color: theme.palette.success.main,
      demo: {
        title: translate('onboarding.demo.features.monitoring.demoTitle'),
        items: [
          { label: translate('onboarding.demo.features.monitoring.zones'), value: translate('onboarding.demo.features.monitoring.zonesValue'), trend: 'up' },
          { label: translate('onboarding.demo.features.monitoring.alertsDetected'), value: translate('onboarding.demo.features.monitoring.alertsValue') },
          { label: translate('onboarding.demo.features.monitoring.coverage'), value: translate('onboarding.demo.features.monitoring.coverageValue') }
        ]
      }
    }
  ];

  useEffect(() => {
    if (isAutoPlaying && autoPlayIndex < features.length) {
      const timer = setTimeout(() => {
        handleFeatureDemo(features[autoPlayIndex].id);
        setAutoPlayIndex(autoPlayIndex + 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (autoPlayIndex >= features.length) {
      setIsAutoPlaying(false);
    }
  }, [autoPlayIndex, isAutoPlaying]);

  const handleFeatureDemo = (featureId: string) => {
    // Open video for the feature
    const videoMap: Record<string, 'company' | 'dashboard' | 'monitoring'> = {
      'tracking': 'company',
      'alerts': 'dashboard',
      'analytics': 'monitoring'
    };
    setCurrentVideo(videoMap[featureId]);
    setVideoDialogOpen(true);

    // Mark as completed after video dialog is opened
    setCompletedFeatures(prev => {
      if (!prev.includes(featureId)) {
        return [...prev, featureId];
      }
      return prev;
    });
  };

  const progress = (completedFeatures.length / features.length) * 100;
  const allCompleted = completedFeatures.length === features.length;

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
      {/* Intro Video Section */}
      {showIntroVideo ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>
              {translate('onboarding.demo.video.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              {translate('onboarding.demo.video.description')}
            </Typography>

            {/* Video Player */}
            <Card
              sx={{
                maxWidth: 800,
                mx: 'auto',
                overflow: 'hidden',
                boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
                mb: 4
              }}
            >
              <VideoPlayer
                videoUrl="/demo-videos/safee-presentation.mp4"
                autoPlay={false}
                muted={false}
              />
            </Card>

            <Button
              variant="contained"
              size="large"
              onClick={() => setShowIntroVideo(false)}
              endIcon={<ArrowForward />}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                }
              }}
            >
              {translate('onboarding.demo.video.discoverFeatures')}
            </Button>

            <Box sx={{ mt: 2 }}>
              <Button
                variant="text"
                size="small"
                onClick={() => setShowIntroVideo(false)}
                sx={{ color: 'text.secondary' }}
              >
                {translate('onboarding.demo.video.skip')}
              </Button>
            </Box>
          </Box>
        </motion.div>
      ) : (
        <>
          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Box
                sx={{
                  display: 'inline-block',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.1) rotate(10deg)'
                  }
                }}
              >
                <Typography variant="h1" sx={{ fontSize: '4rem', mb: 2 }}>
                  ✨
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>
                {translate('onboarding.demo.hero.title')}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {translate('onboarding.demo.hero.subtitle')}
              </Typography>
            </Box>
          </motion.div>

          {/* Progress Bar */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 4,
              background: theme.palette.mode === 'dark'
                ? `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.1)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`
                : `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.05)} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              borderRadius: 2
            }}
          >
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle1" fontWeight={600} color="primary">
                  {translate('onboarding.demo.progress.title')}
                </Typography>
                <Chip
                  icon={<CheckCircle />}
                  label={translate('onboarding.demo.progress.completed', { progress: Math.round(progress) })}
                  color={progress === 100 ? "success" : "primary"}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    '& .MuiChip-icon': {
                      fontSize: 16
                    }
                  }}
                />
              </Box>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 12,
                  borderRadius: 6,
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 6,
                    background: progress === 100
                      ? `linear-gradient(90deg, ${theme.palette.success.light} 0%, ${theme.palette.success.main} 100%)`
                      : `linear-gradient(90deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`
                  }
                }}
              />
            </Stack>
          </Paper>

      {/* Features Grid */}
      <AnimatePresence mode="wait">
        {!allCompleted && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{
              opacity: 0,
              scale: 0.8,
              transition: { duration: 0.5, ease: "easeInOut" }
            }}
          >
            <Grid container spacing={3} sx={{ mb: 6 }}>
              {features.map((feature, index) => (
                <Grid item xs={12} md={4} key={feature.id}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{
                      opacity: 0,
                      y: -20,
                      scale: 0.9,
                      transition: { delay: index * 0.05 }
                    }}
                    transition={{ delay: index * 0.1 }}
                  >
              <Card
                sx={{
                  p: 2.5,
                  height: 280,
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'visible',
                  background: theme.palette.mode === 'dark'
                    ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`
                    : 'white',
                  border: `2px solid ${
                    completedFeatures.includes(feature.id)
                      ? theme.palette.success.main
                      : alpha(theme.palette.divider, 0.3)
                  }`,
                  boxShadow: completedFeatures.includes(feature.id)
                    ? `0 8px 24px ${alpha(theme.palette.success.main, 0.2)}`
                    : `0 4px 12px ${alpha(theme.palette.common.black, 0.05)}`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-6px) scale(1.02)',
                    boxShadow: `0 16px 32px ${alpha(feature.color, 0.2)}`,
                    border: `2px solid ${alpha(feature.color, 0.5)}`,
                    '& .feature-icon': {
                      transform: 'scale(1.08) rotate(5deg)'
                    }
                  }
                }}
                onClick={() => !isAutoPlaying && handleFeatureDemo(feature.id)}
              >
                {completedFeatures.includes(feature.id) && (
                  <Zoom in>
                    <Avatar
                      sx={{
                        position: 'absolute',
                        top: -10,
                        right: -10,
                        width: 32,
                        height: 32,
                        bgcolor: theme.palette.success.main
                      }}
                    >
                      <CheckCircle sx={{ fontSize: 20, color: 'white' }} />
                    </Avatar>
                  </Zoom>
                )}

                <Stack spacing={2} alignItems="center" textAlign="center" sx={{ flex: 1, justifyContent: 'space-between' }}>
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box
                      className="feature-icon"
                      sx={{
                        transition: 'transform 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        bgcolor: alpha(feature.color, 0.1),
                        mb: 2
                      }}
                    >
                      <Typography variant="h2" sx={{ fontSize: '2rem' }}>
                        {feature.emoji}
                      </Typography>
                    </Box>

                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5, fontSize: '1rem' }}>
                      {feature.title}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ flex: 1, display: 'flex', alignItems: 'center', fontSize: '0.875rem', lineHeight: 1.4 }}>
                      {feature.description}
                    </Typography>
                  </Box>

                  <Button
                    variant={completedFeatures.includes(feature.id) ? "outlined" : "contained"}
                    size="small"
                    startIcon={completedFeatures.includes(feature.id) ? <CheckCircle sx={{ fontSize: 16 }} /> : <PlayArrow sx={{ fontSize: 16 }} />}
                    disabled={isAutoPlaying}
                    sx={{
                      mt: 1.5,
                      borderRadius: 1.5,
                      px: 1.5,
                      py: 0.75,
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      fontWeight: 600,
                      bgcolor: completedFeatures.includes(feature.id)
                        ? 'transparent'
                        : alpha(feature.color, 0.9),
                      borderColor: completedFeatures.includes(feature.id)
                        ? theme.palette.success.main
                        : feature.color,
                      color: completedFeatures.includes(feature.id)
                        ? theme.palette.success.main
                        : 'white',
                      '&:hover': {
                        bgcolor: completedFeatures.includes(feature.id)
                          ? alpha(theme.palette.success.main, 0.1)
                          : feature.color,
                        transform: 'scale(1.03)'
                      },
                      '&:disabled': {
                        opacity: 0.5
                      }
                    }}
                  >
                    {completedFeatures.includes(feature.id) ? translate('onboarding.demo.button.done') : translate('onboarding.demo.button.viewDemo')}
                  </Button>
                </Stack>

              </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA Section */}
      <AnimatePresence>
        {allCompleted && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{
              opacity: 1,
              y: 0,
              scale: 1,
              transition: {
                duration: 0.8,
                ease: [0.4, 0, 0.2, 1],
                staggerChildren: 0.1
              }
            }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
          >
            <Box sx={{ textAlign: 'center', mt: 6 }}>
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{
                  scale: 1,
                  rotate: 0,
                  transition: {
                    type: "spring",
                    stiffness: 200,
                    damping: 15,
                    delay: 0.2
                  }
                }}
              >
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    mx: 'auto',
                    mb: 3,
                    bgcolor: theme.palette.success.main,
                    boxShadow: `0 0 40px ${alpha(theme.palette.success.main, 0.4)}`,
                    '@keyframes pulse': {
                      '0%': {
                        boxShadow: `0 0 20px ${alpha(theme.palette.success.main, 0.4)}`
                      },
                      '50%': {
                        boxShadow: `0 0 40px ${alpha(theme.palette.success.main, 0.6)}`
                      },
                      '100%': {
                        boxShadow: `0 0 20px ${alpha(theme.palette.success.main, 0.4)}`
                      }
                    },
                    animation: 'pulse 2s infinite'
                  }}
                >
                  <EmojiEvents sx={{ fontSize: 40, color: 'white' }} />
                </Avatar>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{
                  opacity: 1,
                  y: 0,
                  transition: { delay: 0.4, duration: 0.6 }
                }}
              >
                <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                  {translate('onboarding.demo.complete.title')}
                </Typography>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{
                  opacity: 1,
                  y: 0,
                  transition: { delay: 0.5, duration: 0.6 }
                }}
              >
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                  {translate('onboarding.demo.complete.description')}
                </Typography>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{
                  opacity: 1,
                  scale: 1,
                  transition: {
                    delay: 0.6,
                    duration: 0.5,
                    type: "spring",
                    stiffness: 100
                  }
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={onComplete}
                  endIcon={<ArrowForward />}
                  sx={{
                    px: 4,
                    py: 2,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
                    '&:hover': {
                      transform: 'translateY(-2px) scale(1.05)',
                      boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.4)}`,
                      transition: 'all 0.3s ease'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  {translate('onboarding.demo.complete.cta')}
                </Button>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{
                  opacity: 1,
                  y: 0,
                  transition: { delay: 0.8, duration: 0.6 }
                }}
              >
                <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 3 }}>
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    transition={{ type: "spring", stiffness: 400 }}
                  >
                    <Chip icon={<AutoAwesome />} label={translate('onboarding.demo.complete.setupDone')} color="success" />
                  </motion.div>
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    transition={{ type: "spring", stiffness: 400 }}
                  >
                    <Chip icon={<Speed />} label={translate('onboarding.demo.complete.readyToStart')} color="primary" />
                  </motion.div>
                </Stack>
              </motion.div>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
        </>
      )}

      {/* Video Dialog */}
      <Dialog
        open={videoDialogOpen}
        onClose={() => setVideoDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            bgcolor: 'transparent',
            backgroundImage: 'none',
            boxShadow: `0 25px 50px -12px ${alpha(theme.palette.common.black, 0.5)}`
          }
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backdropFilter: 'blur(10px)',
            bgcolor: alpha(theme.palette.common.black, 0.7)
          }
        }}
      >
        <Box sx={{ position: 'relative', bgcolor: theme.palette.background.paper }}>
          <IconButton
            onClick={() => setVideoDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 16,
              top: 16,
              color: 'white',
              bgcolor: alpha(theme.palette.common.black, 0.6),
              zIndex: 10,
              '&:hover': {
                bgcolor: alpha(theme.palette.common.black, 0.8),
                transform: 'scale(1.1)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            <Close />
          </IconButton>
          <DialogContent sx={{ p: 0, position: 'relative' }}>
            {currentVideo && (
              <Box sx={{
                aspectRatio: '16/9',
                width: '100%',
                bgcolor: 'black'
              }}>
                <VideoPlayer
                  videoId={currentVideo}
                  autoPlay={true}
                  onEnded={() => setVideoDialogOpen(false)}
                />
              </Box>
            )}
          </DialogContent>
        </Box>
      </Dialog>
    </Box>
  );
};
